import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Carousel } from 'primeng/carousel';

import { DriverStats, Month } from '@app/core/model/qualifications.model';
import { IBonusQualificationService } from '@app/core/services/interface/IBonusQualificationService';
import { IConfigurationService } from '@app/core/services/interface/IConfigurationService';
import { ServiceFactory } from '@app/core/services/service.factory';
import { AuthService } from '@app/core/auth.service';
import { IDriver360Service } from '@app/core/services/interface/IDriver360Service';

import {
  AccidentDetail,
  BonusDetail,
  CommitmentDetail,
  CriticalEventDetail,
  LateArrivalDetail,
  SafetyVideoDetail,
  WigAttendanceDetail,
} from './wig-stats.model';
import { WigStatsService } from './wig-stats.service';

@Component({
  selector: 'app-wig-stats',
  templateUrl: './wig-stats.component.html',
  styleUrls: ['./wig-stats.component.css'],
})
export class WigStatsComponent implements AfterViewChecked, OnInit {
  @ViewChild('monthlyStatsCarousel') monthlyStatsCarousel: Carousel;
  @ViewChild('statPanelGrid') statPanel: ElementRef<HTMLDivElement>;
  accidentDetails: AccidentDetail[];
  bonusQualificationService: IBonusQualificationService;
  calculateMargin: boolean;
  commitmentDetails: CommitmentDetail[];
  configurationService: IConfigurationService;
  criticalEventsDetails: CriticalEventDetail[];
  currentMonth: Month;
  currentMonthIndex: number;
  defaultMonthIndex: number;
  driver360Service: IDriver360Service;
  driverCode: string;
  driverStats: DriverStats[];
  isAccidentSidebarVisible: boolean;
  isCommitmentSidebarVisible: boolean;
  isCriticalEventsSidebarVisible: boolean;
  isLateArrivalsSidebarVisible: boolean;
  isLoading: boolean;
  isLoadingDetails: boolean;
  isSafetyVideoSidebarVisible: boolean;
  isSidebarVisible: boolean;
  isWigAttendanceSidebarVisible: boolean;
  isDVIRSidebarVisible: boolean;
  isMissingPaperworkSidebarVisible: boolean;
  isCSAViolationSidebarVisible: boolean;
  isLogViolationSidebarVisible: boolean;
  lastMonthIndex: number;
  lateArrivalsDetails: LateArrivalDetail[];
  monthList: Month[];
  percentageSafetyVideoCompleted = 100;
  safetyVideosDetails: BonusDetail[];
  statPanelMargin: number;
  wigAttendanceDetails: WigAttendanceDetail[];
  missingDVRDetails: BonusDetail[];
  missingPaperworkDetails: BonusDetail[];
  csaViolationDetails: BonusDetail[];
  logViolationDetails: BonusDetail[];

  constructor(
    private authService: AuthService,
    private wigStatsService: WigStatsService,
    serviceFactory: ServiceFactory
  ) {
    this.accidentDetails = [];
    this.commitmentDetails = [];
    this.criticalEventsDetails = [];
    this.currentMonth = null;
    this.currentMonthIndex = 0;
    this.defaultMonthIndex = -1;
    this.driverStats = [];
    this.isAccidentSidebarVisible = false;
    this.isCommitmentSidebarVisible = false;
    this.isCriticalEventsSidebarVisible = false;
    this.isLateArrivalsSidebarVisible = false;
    this.isLoading = false;
    this.isLoadingDetails = false;
    this.isSafetyVideoSidebarVisible = false;
    this.isSidebarVisible = false;
    this.isWigAttendanceSidebarVisible = false;
    this.isDVIRSidebarVisible = false;
    this.isMissingPaperworkSidebarVisible = false;
    this.isCSAViolationSidebarVisible = false;
    this.isLogViolationSidebarVisible = false;
    this.lastMonthIndex = 0;
    this.lateArrivalsDetails = [];
    this.monthList = [];
    this.safetyVideosDetails = [];
    this.wigAttendanceDetails = [];
    this.missingDVRDetails = [];
    this.missingPaperworkDetails = [];
    this.logViolationDetails = [];
    this.csaViolationDetails = [];
    this.statPanelMargin = 0;
    this.calculateMargin = true;
    this.configurationService = serviceFactory.getConfigurationService();
    this.bonusQualificationService = serviceFactory.getBonusQualificationService();
    this.driver360Service = serviceFactory.getDriver360Service();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.calculateMargin = true;
    // console.log('resize');
  }

  clearSidebar(): void {
    this.hideSidebar();
    this.accidentDetails = [];
    this.commitmentDetails = [];
    this.criticalEventsDetails = [];

    this.isAccidentSidebarVisible = false;
    this.isCommitmentSidebarVisible = false;
    this.isCriticalEventsSidebarVisible = false;
    this.isLateArrivalsSidebarVisible = false;
    this.isSafetyVideoSidebarVisible = false;
    this.isWigAttendanceSidebarVisible = false;
    this.isCSAViolationSidebarVisible = false;
    this.isMissingPaperworkSidebarVisible = false;
    this.isLogViolationSidebarVisible = false;
    this.isDVIRSidebarVisible = false;

    this.lateArrivalsDetails = [];
    this.safetyVideosDetails = [];
    this.wigAttendanceDetails = [];
    this.missingDVRDetails = [];
    this.missingPaperworkDetails = [];
    this.logViolationDetails = [];
    this.csaViolationDetails = [];
  }

  displayAccidentDetails(): void {
    this.isAccidentSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getAccidentsDetails(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        const mappedAccidents: AccidentDetail[] = [];
        for (const detail of data) {
          let locationToSet = null;
          let typeToSet = null;
          const descriptionSplit = detail.description.split('. ');
          if (descriptionSplit.length > 1) {
            locationToSet = descriptionSplit[0];
            typeToSet = descriptionSplit[1];
          } else {
            locationToSet = descriptionSplit[0];
          }

          mappedAccidents.push({
            date: detail.detailDate,
            accidentType: typeToSet,
            location: locationToSet,
          });
        }

        this.accidentDetails = mappedAccidents;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayCommitmentDetails(leadId: number): void {
    this.isCommitmentSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getCommitmentDetailsById(
        this.driverCode,
        leadId,
        this.currentMonth.startDate,
        this.currentMonth.endDate
      )
      .then((data) => {
        this.commitmentDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayCriticalEventsDetails(): void {
    this.isCriticalEventsSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getCriticalEventsDetails(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        const mappedCriticalEvents: CriticalEventDetail[] = [];
        for (const detail of data) {
          let locationToSet = null;
          let observationToSet = null;
          const descriptionSplit = detail.description.split(' : ');
          if (descriptionSplit.length > 1) {
            observationToSet = descriptionSplit[0];
            locationToSet = descriptionSplit[1];
          } else {
            observationToSet = descriptionSplit[0];
          }

          mappedCriticalEvents.push({
            recordedDateTime: detail.detailDate,
            observations: observationToSet,
            location: locationToSet,
          });
        }

        this.criticalEventsDetails = mappedCriticalEvents;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayLogViolation(): void {
    this.isLogViolationSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getLogViolationDetails(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        this.logViolationDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayDVR(): void {
    this.isDVIRSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getMissingDVR(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        this.missingDVRDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayCSAViolation(): void {
    this.isCSAViolationSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getCSAViolationDetails(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        this.csaViolationDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayMissingPaperwok(): void {
    this.isMissingPaperworkSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;
    console.log('here');

    this.wigStatsService
      .getMissingPaperwork(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        this.missingPaperworkDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayHuddleSidebar(): void {
    this.isWigAttendanceSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getWigAttendanceDetails(
        this.driverCode,
        this.currentMonth.startDate,
        this.currentMonth.endDate
      )
      .then((data) => {
        this.wigAttendanceDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displayLateArrivalsDetails(): void {
    this.isLateArrivalsSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getLateArrivalsDetails(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        const mappedLateArrivals: LateArrivalDetail[] = [];
        for (const detail of data) {
          let locationToSet = null;
          let tripToSet = null;
          const descriptionSplit = detail.description.split(' : ');
          if (descriptionSplit.length > 1) {
            locationToSet = descriptionSplit[0];
            tripToSet = descriptionSplit[1];
          } else {
            locationToSet = descriptionSplit[0];
          }

          mappedLateArrivals.push({
            date: detail.detailDate,
            trip: tripToSet,
            location: locationToSet,
          });
        }

        this.lateArrivalsDetails = mappedLateArrivals;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  displaySafetyVideoDetails(): void {
    this.isSafetyVideoSidebarVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;

    this.wigStatsService
      .getSafetyVideosDetails(this.driverCode, this.currentMonth.startDate)
      .then((data) => {
        this.safetyVideosDetails = data;
      })
      .finally(() => {
        this.isLoadingDetails = false;
      });
  }

  hideSidebar(): void {
    this.isSidebarVisible = false;
  }

  async loadDisplayableMonths(): Promise<void> {
    const displayableMonths = await this.configurationService.getDisplaypayablemonths();
    this.monthList = displayableMonths.map((value: Month) => {
      const date = new Date(value.startDate);
      date.setDate(date.getDate() + 1);
      return {
        startDate: value.startDate,
        endDate: value.endDate,
        month: date.toLocaleString('default', { month: 'long' }),
      };
    });
    this.currentMonthIndex = this.monthList.findIndex((value) => {
      return value.month === new Date().toLocaleString('default', { month: 'long' });
    });
    this.currentMonth = this.monthList[this.currentMonthIndex];
  }

  async loadDriverStats(): Promise<void> {
    this.isLoading = true;

    const driverStats = await this.bonusQualificationService.getDriverStats(this.driverCode);
    this.driverStats = driverStats.map((x) => new DriverStats(x));

    this.lastMonthIndex = driverStats.length - 1;

    for (let i = 0; i < driverStats.length; i++) {
      if (driverStats[i].qualifyPeriod === this.currentMonth.startDate) {
        this.defaultMonthIndex = i;
      }
    }

    if (this.defaultMonthIndex === -1) {
      this.defaultMonthIndex = this.lastMonthIndex;
    }
    this.currentMonthIndex = this.defaultMonthIndex;
    this.isLoading = false;
  }

  nextMonth(): void {
    if (this.currentMonthIndex < this.lastMonthIndex) {
      this.currentMonthIndex++;
      this.currentMonth = this.monthList[this.currentMonthIndex];

      this.monthlyStatsCarousel.navForward(this, this.currentMonthIndex);
    }
  }

  ngOnInit(): void {
    this.driverCode = this.authService.getCurrentUser().username;
    void (async () => {
      await this.loadDisplayableMonths();
      await this.loadDriverStats();

      this.monthlyStatsCarousel.onTouchMove = () => {};
      this.monthlyStatsCarousel.onTouchEnd = () => {};
    })();
  }

  ngAfterViewChecked(): void {
    if (this.calculateMargin && this.statPanel) {
      setTimeout(() => {
        const marginPerRow = this.statPanel.nativeElement.offsetWidth % 102;
        // console.log(marginPerRow / 2);
        this.statPanelMargin = marginPerRow / 2;
        this.calculateMargin = false;
      });
    }
  }

  previousMonth(): void {
    if (this.currentMonthIndex > 0) {
      this.currentMonthIndex--;
      this.currentMonth = this.monthList[this.currentMonthIndex];

      this.monthlyStatsCarousel.navBackward(this, this.currentMonthIndex);
    }
  }
}
